import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import {
  getAiTextAnalysisConfigApi,
  updateAiTextAnalysisConfigApi,
} from '@/lib/reports-api';
import type { AiTextAnalysisConfig } from '@/lib/types';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';

/**
 * Issue #227 — 管理员「系统设置 → AI文本分析」。
 *
 * 真实 GET/PATCH /system/ai-text-config（ADMIN+，按 orgId 隔离）。配置通话质检
 * DeepSeek 分析输出的「建议话术 / 问题点」可见字符上下限：默认 20/1000。运行时
 * 读取即时生效，无需重启。最大值不得小于最小值，违反由服务端返回 400 明确错误。
 */
const INPUT =
  'w-28 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';

type Draft = Record<keyof AiTextAnalysisConfig, string>;

function toDraft(c: AiTextAnalysisConfig): Draft {
  return {
    prompt: c.prompt ?? '',
    suggestedScriptMinChars: String(c.suggestedScriptMinChars),
    suggestedScriptMaxChars: String(c.suggestedScriptMaxChars),
    problemPointMinChars: String(c.problemPointMinChars),
    problemPointMaxChars: String(c.problemPointMaxChars),
    aiScoringTimeoutMinutes: String(c.aiScoringTimeoutMinutes),
  };
}

export function AiTextAnalysisSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['system', 'ai-text-config'],
    queryFn: getAiTextAnalysisConfigApi,
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data?.config) setDraft(toDraft(query.data.config));
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (body: Partial<AiTextAnalysisConfig>) =>
      updateAiTextAnalysisConfigApi(body),
    onSuccess: async (data) => {
      setSaveError(null);
      setSaved(true);
      setDraft(toDraft(data.config));
      await queryClient.invalidateQueries({ queryKey: ['system', 'ai-text-config'] });
    },
    onError: (err) =>
      setSaveError(
        err instanceof ApiError
          ? `${err.message}（HTTP ${err.status}）`
          : '保存失败，请重试。',
      ),
  });

  if (query.isLoading) {
    return (
      <Card title="AI文本分析">
        <LoadingState label="正在加载 AI 文本分析配置…" />
      </Card>
    );
  }
  if (query.isError || !draft) {
    return (
      <Card title="AI文本分析">
        <ErrorState
          title="加载 AI 文本分析配置失败"
          description={query.error instanceof ApiError ? query.error.message : '请稍后重试。'}
          retry={() => void query.refetch()}
        />
      </Card>
    );
  }

  const set = (key: keyof Draft, value: string) => {
    setSaved(false);
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  // 前端先行提示「最大值不得小于最小值」，最终仍以服务端校验为准。
  const ssMin = Number(draft.suggestedScriptMinChars);
  const ssMax = Number(draft.suggestedScriptMaxChars);
  const ppMin = Number(draft.problemPointMinChars);
  const ppMax = Number(draft.problemPointMaxChars);
  const timeoutMinutes = Number(draft.aiScoringTimeoutMinutes);
  const localInvalid =
    ssMax < ssMin ||
    ppMax < ppMin ||
    !Number.isFinite(timeoutMinutes) ||
    timeoutMinutes < 1 ||
    timeoutMinutes > 120;

  const promptValue = draft.prompt ?? '';
  const onSave = () => {
    setSaved(false);
    setSaveError(null);
    mutation.mutate({
      // Issue #271：提示词随配置一同提交；空白由服务端归一化回退出厂默认。
      prompt: promptValue,
      suggestedScriptMinChars: ssMin,
      suggestedScriptMaxChars: ssMax,
      problemPointMinChars: ppMin,
      problemPointMaxChars: ppMax,
      aiScoringTimeoutMinutes: timeoutMinutes,
    });
  };

  const defaultPrompt = query.data?.defaults?.prompt ?? '';

  return (
    <Card
      title="AI文本分析"
      description="配置通话质检 DeepSeek 分析提示词、建议话术/问题点字数，以及长文本 AI 评分超时时长。保存后重跑 AI 质检即时生效，无需重启。若 AI 确无问题 / 无建议，报告显示统一备注「AI诊断无需建议/问题点」。"
    >
      {/* Issue #271 — AI 文本分析提示词（DeepSeek 质检分析人设）。textarea 自动换行、默认高 200px。 */}
      <div className="mb-4">
        <div className="mb-1 flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700" htmlFor="ai-text-prompt">
            AI 文本分析提示词
          </label>
          {canEdit && defaultPrompt && promptValue.trim() !== defaultPrompt.trim() ? (
            <button
              type="button"
              className="text-xs text-brand-600 hover:underline"
              onClick={() => set('prompt', defaultPrompt)}
              data-testid="ai-text-prompt-reset"
            >
              恢复默认提示词
            </button>
          ) : null}
        </div>
        <textarea
          id="ai-text-prompt"
          className="block w-full whitespace-pre-wrap break-words rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50 disabled:text-slate-400"
          style={{ height: 200 }}
          value={promptValue}
          maxLength={4000}
          disabled={!canEdit}
          placeholder={defaultPrompt}
          onChange={(e) => set('prompt', e.target.value)}
          data-testid="ai-text-prompt"
        />
        <p className="mt-1 text-xs text-slate-400">
          作为 DeepSeek 质检分析的人设/视角；系统会自动附加「只产出问题点/客户意向/建议话术 + 严格 JSON」的输出约束。留空则使用出厂默认。最长 4000 字符。
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Row label="建议话术最少字数" hint="默认 20">
          <input
            className={INPUT}
            type="number"
            min={1}
            value={draft.suggestedScriptMinChars}
            disabled={!canEdit}
            onChange={(e) => set('suggestedScriptMinChars', e.target.value)}
            data-testid="ai-text-script-min"
          />
        </Row>
        <Row label="建议话术最多字数" hint="默认 1000">
          <input
            className={INPUT}
            type="number"
            min={1}
            value={draft.suggestedScriptMaxChars}
            disabled={!canEdit}
            onChange={(e) => set('suggestedScriptMaxChars', e.target.value)}
            data-testid="ai-text-script-max"
          />
        </Row>
        <Row label="问题点最少字数" hint="默认 20">
          <input
            className={INPUT}
            type="number"
            min={1}
            value={draft.problemPointMinChars}
            disabled={!canEdit}
            onChange={(e) => set('problemPointMinChars', e.target.value)}
            data-testid="ai-text-problem-min"
          />
        </Row>
        <Row label="问题点最多字数" hint="默认 1000">
          <input
            className={INPUT}
            type="number"
            min={1}
            value={draft.problemPointMaxChars}
            disabled={!canEdit}
            onChange={(e) => set('problemPointMaxChars', e.target.value)}
            data-testid="ai-text-problem-max"
          />
        </Row>
        <Row label="AI评分超时时长" hint="默认 5 分钟，最大 120 分钟">
          <input
            className={INPUT}
            type="number"
            min={1}
            max={120}
            value={draft.aiScoringTimeoutMinutes}
            disabled={!canEdit}
            onChange={(e) => set('aiScoringTimeoutMinutes', e.target.value)}
            data-testid="ai-text-scoring-timeout"
          />
        </Row>
      </div>

      {localInvalid ? (
        <p className="mt-3 text-xs text-amber-600">
          最大字数不得小于最小字数；AI 评分超时时长需为 1-120 分钟。
        </p>
      ) : null}

      {canEdit ? (
        <div className="mt-4 flex items-center gap-3">
          <button
            className={PRIMARY_BTN}
            onClick={onSave}
            disabled={mutation.isPending || localInvalid}
          >
            {mutation.isPending ? '保存中…' : '保存配置'}
          </button>
          {saved ? (
            <span className="text-xs text-emerald-600">已保存，重跑 AI 质检即时生效。</span>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 text-xs text-slate-500">仅 ADMIN 及以上角色可修改 AI 文本分析配置。</p>
      )}
      {saveError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {saveError}
        </div>
      ) : null}
    </Card>
  );
}

function Row({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm">
      <span className="text-slate-600">
        {label}
        <span className="ml-1 text-xs text-slate-400">（{hint}）</span>
      </span>
      {children}
    </label>
  );
}
